import { useMemo } from 'react';
import { routesPath } from '@router/routes';
import { useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { SubjectListView } from './SubjectList.view';
import { SiteSubjectTypeForm } from '@components/modals/SubjectCreate/SubjectCreate';
import { twJoin } from '@utils/twMerge';
import { SiteType } from '@dynamika-shared/types/site';
import { useSubjectListFilters } from './useSubjectListFilters';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import {
  createNewSubjectSuccess,
  useSiteSubjectsListSlice,
} from '@restore/SiteSubjectsList/SiteSubjectsList.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { useClinicalTrialAffiliatesSlice } from '@restore/clinicalTrialAffiliates/clinicalTrialAffiliates.slice';
import { waitForActions } from '@restore/helpers';
import { loadingTabItems } from 'src/pages/SiteUser/SubjectList/LoadingState/loadingTabItems';

export const SubjectList = () => {
  const { id: clinicalTrialId, siteId } = useParams<{
    id: string;
    siteId: string;
  }>();

  const [contextData, loadingContext] = useClinicalTrialContextSlice(
    (state, actions) => [
      state.data,
      state.loading,
      actions.fetchClinicalTrialContext,
    ]
  );

  const clinicalTrialAffiliatesData = useClinicalTrialAffiliatesSlice(
    state => state.data
  );

  const [
    siteSubjectsList,
    loading,
    newlyCreatedSubjectCode,
    createSubject,
    clearNewlyCreatedSubject,
  ] = useSiteSubjectsListSlice((state, actions) => [
    state.data,
    state.loading,
    state.newlyCreatedSubjectCode,
    actions.createNewSubject,
    actions.clearNewlyCreatedSubject,
    actions.onNewlyCreatedSubject,
  ]);

  const { onSort, onSearch, setSortBy, sortBy } = useSubjectListFilters();

  useBreadcrumbs([['Home', 'home']]);

  const navigate = useNavigate();

  const selectedSite = useMemo(
    () => contextData?.affiliatedSiteList.find(site => site.id === siteId),
    [contextData?.affiliatedSiteList, siteId]
  );

  const selectedClinicalTrial = useMemo(
    () => clinicalTrialAffiliatesData.find(ct => ct.id === clinicalTrialId),
    [clinicalTrialAffiliatesData, clinicalTrialId]
  );

  const tabsItems = useMemo(
    () =>
      contextData?.affiliatedSiteList.map((site: SiteType) => {
        return {
          key: site.id,
          label: (
            <TruncatedText
              text={site.code}
              truncate
              tooltip
              className={twJoin(
                'text-1624 text-dimGray font-medium max-w-[15rem]',
                site.id === siteId && 'text-dodgerBlue'
              )}
            />
          ),
          children: null,
        };
      }),
    [contextData?.affiliatedSiteList, siteId]
  );

  const handleTabChange = (key: string) => {
    if (!clinicalTrialId) return;
    setSortBy({ listName: 'subjectList', newSortBy: undefined });

    if (contextData) {
      const selectedSite = contextData.affiliatedSiteList.find(
        (site: SiteType) => site.id === key
      );

      if (selectedSite) {
        navigate(`${routesPath.subjectBase}/${clinicalTrialId}/${key}`);
      }
    }
  };

  const onCreateNewSubject = async (data: SiteSubjectTypeForm) => {
    if (clinicalTrialId && siteId) {
      createSubject({
        data,
        clinicalTrialId,
        siteId,
      });
      await waitForActions(createNewSubjectSuccess);
    }
  };

  if (!siteId || !clinicalTrialId) {
    return null;
  }

  return (
    <SubjectListView
      tabsItems={loadingContext ? loadingTabItems : tabsItems}
      handleTabChange={handleTabChange}
      data={siteSubjectsList}
      loading={loading}
      clinicalTrialName={selectedClinicalTrial?.name}
      clinicalTrialId={clinicalTrialId}
      siteId={siteId}
      siteCode={selectedSite?.code}
      onSort={onSort}
      onHighlightFinish={clearNewlyCreatedSubject}
      onCreateNewSubject={onCreateNewSubject}
      configStatus={contextData?.configuration.status}
      cohorts={contextData?.configuration.cohorts ?? []}
      newlyCreatedSubjectCode={newlyCreatedSubjectCode}
      sortState={sortBy}
      onSearch={onSearch}
    />
  );
};
